<!-- src/views/projects/MultiItemInspectionFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & PROTOCOL METADATA -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden text-left">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none"><ShieldCheckIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <span class="px-3 py-1 bg-[#20FCF1]/10 text-[#20FCF1] border border-[#20FCF1]/30 rounded-full text-[9px] font-black uppercase tracking-widest">Protocol SS-PEI-2</span>
                <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">MSPRG010.1 Rev 2.0</span>
              </div>
              <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none">Pre-Use Plant & Equipment Inspection</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Multi-Item Shift Check (Up to 5 Items) • Worksite Log</p>
            </div>

            <div class="bg-white/5 p-4 rounded-2xl border border-white/10 flex items-center gap-4">
              <div v-if="qrCodeUrl" class="h-16 w-16 bg-white p-1 rounded-xl shrink-0 flex items-center justify-center">
                <img :src="qrCodeUrl" alt="QR" class="w-full h-full object-contain" />
              </div>
              <div>
                <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">Active Record</p>
                <p class="text-sm font-black text-white uppercase mt-1">{{ formReference }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. FORM BODY -->
        <div class="bg-white p-6 sm:p-10 md:p-14 rounded-b-[40px] shadow-xl border-x border-b border-slate-100 text-left">
          
          <form @submit.prevent="submitForm" class="space-y-16">

            <!-- SECTION 1: SITE & SHIFT DETAILS -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">1</div>
                <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Site & Shift Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
                <div>
                  <label class="form-label">Site / Location *</label>
                  <input type="text" v-model="formData.siteDetails.siteLocation" required placeholder="Project Site Name" class="form-input" />
                </div>
                <div>
                  <label class="form-label">Inspection Date *</label>
                  <input type="date" v-model="formData.siteDetails.date" required class="form-input" />
                </div>
                <div>
                  <label class="form-label">Shift / Time *</label>
                  <input type="text" v-model="formData.siteDetails.shiftTime" required placeholder="e.g. Day Shift 07:00 - 17:00" class="form-input" />
                </div>
                <div class="md:col-span-3">
                  <label class="form-label">Inspected By (Inspector Name) *</label>
                  <input type="text" v-model="formData.siteDetails.inspectedBy" required placeholder="Inspector Full Name" class="form-input" />
                </div>
              </div>
            </section>

            <!-- SECTION 2: ITEMS INSPECTED (UP TO 5 ITEMS) -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">2</div>
                <div>
                  <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Items Inspected (Max 5 Items)</h2>
                  <p class="text-xs text-slate-500 font-medium">Complete one row per asset checked during this shift.</p>
                </div>
              </div>

              <div class="space-y-6">
                <div v-for="(item, idx) in formData.items" :key="idx" class="bg-slate-50 p-6 rounded-[32px] border border-slate-200 shadow-sm space-y-4">
                  <div class="flex items-center justify-between border-b border-slate-200 pb-3">
                    <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest bg-white px-3 py-1 rounded-lg border border-slate-200 shadow-sm">Item #{{ idx + 1 }}</span>
                    <button type="button" @click="clearItemRow(idx)" class="text-[9px] font-black uppercase text-rose-600 hover:text-rose-800 tracking-widest cursor-pointer border-none bg-transparent">Clear Row</button>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label class="text-[9px] font-black uppercase text-slate-500 mb-1 block">Asset ID / Description *</label>
                      <!-- 🛡️ DYNAMIC OPTIONS: Populated from Admin Manage Options registry -->
                      <select v-model="item.assetId" @change="onAssetSelected(idx)" class="form-input !py-3 text-xs font-black uppercase cursor-pointer">
                        <option value="">Select Managed Asset...</option>
                        <option v-for="asset in availableAssets" :key="asset.value" :value="asset.value">
                          {{ asset.value }} - {{ asset.text }}
                        </option>
                      </select>
                    </div>
                    <div>
                      <label class="text-[9px] font-black uppercase text-slate-500 mb-1 block">Serial No.</label>
                      <input type="text" v-model="item.serialNo" placeholder="Serial Number" class="form-input !py-3 text-xs" />
                    </div>
                    <div>
                      <label class="text-[9px] font-black uppercase text-slate-500 mb-1 block">Category</label>
                      <input type="text" v-model="item.category" placeholder="e.g. Survey / Power Tool" class="form-input !py-3 text-xs" />
                    </div>
                    <div>
                      <label class="text-[9px] font-black uppercase text-slate-500 mb-1 block">Shift Result</label>
                      <select v-model="item.result" class="form-input !py-3 text-xs font-black uppercase cursor-pointer" :class="item.result === 'FAIL' || item.result === 'QUARANTINE' ? 'text-rose-600 bg-rose-50' : 'text-emerald-700 bg-emerald-50'">
                        <option value="PASS">PASS – Safe to use</option>
                        <option value="FAIL">FAIL – Do not use</option>
                        <option value="QUARANTINE">QUARANTINE</option>
                      </select>
                    </div>
                  </div>

                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
                    <div class="flex items-center justify-between bg-white px-4 py-2 rounded-xl border border-slate-200">
                      <span class="text-[10px] font-bold text-slate-700">Visual OK?</span>
                      <select v-model="item.visualOk" class="text-xs font-bold outline-none bg-transparent cursor-pointer">
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                        <option value="NA">N/A</option>
                      </select>
                    </div>
                    <div class="flex items-center justify-between bg-white px-4 py-2 rounded-xl border border-slate-200">
                      <span class="text-[10px] font-bold text-slate-700">Guards OK?</span>
                      <select v-model="item.guardsOk" class="text-xs font-bold outline-none bg-transparent cursor-pointer">
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                        <option value="NA">N/A</option>
                      </select>
                    </div>
                    <div class="flex items-center justify-between bg-white px-4 py-2 rounded-xl border border-slate-200">
                      <span class="text-[10px] font-bold text-slate-700">Cables OK?</span>
                      <select v-model="item.cablesOk" class="text-xs font-bold outline-none bg-transparent cursor-pointer">
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                        <option value="NA">N/A</option>
                      </select>
                    </div>
                    <div class="flex items-center justify-between bg-white px-4 py-2 rounded-xl border border-slate-200">
                      <span class="text-[10px] font-bold text-slate-700">Function OK?</span>
                      <select v-model="item.functionOk" class="text-xs font-bold outline-none bg-transparent cursor-pointer">
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                        <option value="NA">N/A</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- SECTION 3: DEFECTS FOUND / ACTIONS TAKEN -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">3</div>
                <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Defects Found / Actions Taken</h2>
              </div>
              <div>
                <textarea v-model="formData.defectsDetails" rows="4" placeholder="Detail any failed or quarantined items, quarantine tag numbers, and actions taken..." class="form-input"></textarea>
              </div>
              <div class="p-6 bg-slate-50 rounded-3xl border border-slate-200">
                <DocumentUploader id="multi_defect_evidence" label="Upload Defect Photo / Quarantine Evidence" @update:files="(files) => handleFileChange('defectEvidence', files)" />
              </div>
            </section>

            <!-- SECTION 4: SUMMARY & SIGN-OFF -->
            <section class="bg-slate-900 p-8 md:p-12 rounded-[40px] text-white shadow-2xl space-y-8">
              <h2 class="text-xl font-black text-[#20FCF1] uppercase tracking-tight">4. Summary & Sign-Off</h2>

              <div class="grid grid-cols-2 md:grid-cols-4 gap-6 bg-white/5 p-6 rounded-3xl border border-white/10 text-center">
                <div>
                  <p class="text-[8px] font-black uppercase tracking-widest text-slate-400">Total Inspected</p>
                  <p class="text-2xl font-black text-white mt-1">{{ formData.items.filter(i => i.assetId).length }}</p>
                </div>
                <div>
                  <p class="text-[8px] font-black uppercase tracking-widest text-emerald-400">Number PASS</p>
                  <p class="text-2xl font-black text-emerald-400 mt-1">{{ formData.items.filter(i => i.assetId && i.result === 'PASS').length }}</p>
                </div>
                <div>
                  <p class="text-[8px] font-black uppercase tracking-widest text-rose-400">Number FAIL / QUARANTINE</p>
                  <p class="text-2xl font-black text-rose-400 mt-1">{{ formData.items.filter(i => i.assetId && (i.result === 'FAIL' || i.result === 'QUARANTINE')).length }}</p>
                </div>
                <div>
                  <p class="text-[8px] font-black uppercase tracking-widest text-[#20FCF1]">All Defects Reported?</p>
                  <p class="text-lg font-black text-[#20FCF1] mt-2">YES</p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label class="text-[10px] font-black uppercase text-[#20FCF1] mb-2 block">Inspector Name (Print) *</label>
                  <input type="text" v-model="formData.signOff.inspectorName" required class="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white font-bold uppercase" />
                </div>
                <div>
                  <label class="text-[10px] font-black uppercase text-[#20FCF1] mb-2 block">Supervisor / Team Leader Name (Print)</label>
                  <input type="text" v-model="formData.signOff.supervisorName" class="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white font-bold uppercase" />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                <div class="bg-white rounded-3xl p-6 text-[#0A1630]">
                  <label class="text-[10px] font-black uppercase text-[#0A1630] mb-3 block">Inspector Digital Signature Seal *</label>
                  <div v-if="inspectorSigPreview" class="mb-4 flex justify-center bg-slate-50 p-4 rounded-xl border">
                    <img :src="inspectorSigPreview" class="max-h-20 object-contain grayscale" alt="Inspector Sig" />
                  </div>
                  <Vue3Signature v-else ref="inspectorSigPad" :sigOption="{ penColor: '#0A1630', backgroundColor: 'rgba(255,255,255,0)' }" :h="'160px'" :w="'100%'" class="border border-slate-200 rounded-xl"></Vue3Signature>
                  <div class="mt-4 flex gap-3">
                    <button type="button" @click="saveInspectorSig" v-if="!inspectorSigPreview" class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl text-[9px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm">Confirm Seal</button>
                    <button type="button" @click="clearInspectorSig" class="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm">Clear</button>
                  </div>
                </div>

                <div class="bg-white rounded-3xl p-6 text-[#0A1630]">
                  <label class="text-[10px] font-black uppercase text-[#0A1630] mb-3 block">Supervisor Digital Signature Seal</label>
                  <div v-if="supervisorSigPreview" class="mb-4 flex justify-center bg-slate-50 p-4 rounded-xl border">
                    <img :src="supervisorSigPreview" class="max-h-20 object-contain grayscale" alt="Supervisor Sig" />
                  </div>
                  <Vue3Signature v-else ref="supervisorSigPad" :sigOption="{ penColor: '#0A1630', backgroundColor: 'rgba(255,255,255,0)' }" :h="'160px'" :w="'100%'" class="border border-slate-200 rounded-xl"></Vue3Signature>
                  <div class="mt-4 flex gap-3">
                    <button type="button" @click="saveSupervisorSig" v-if="!supervisorSigPreview" class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl text-[9px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm">Confirm Seal</button>
                    <button type="button" @click="clearSupervisorSig" class="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-widest cursor-pointer border-none shadow-sm">Clear</button>
                  </div>
                </div>
              </div>
            </section>

            <!-- SUBMIT BUTTON -->
            <div class="pt-6">
              <button type="submit" :disabled="isSubmitting" class="w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[28px] text-lg font-black uppercase tracking-widest shadow-xl hover:scale-[1.01] transition-all flex items-center justify-center gap-3 cursor-pointer border-none">
                <span v-if="isSubmitting" class="animate-spin h-5 w-5 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                <span>{{ isSubmitting ? 'Synchronising Shift Protocol...' : 'Authorise & Submit Multi-Item Shift Check' }}</span>
              </button>
            </div>

          </form>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { db, storage } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Vue3Signature from 'vue3-signature';
import QRCode from 'qrcode';
import DocumentUploader from '@/components/DocumentUploader.vue';
import { ShieldCheckIcon } from '@heroicons/vue/24/outline';

const router = useRouter();
const userStore = useUserStore();
const functions = getFunctions();

const isSubmitting = ref(false);
const qrCodeUrl = ref('');
const formReference = ref('SS-PEI-2-TBC');
const availableAssets = ref([]);

const inspectorSigPad = ref(null);
const inspectorSigPreview = ref(null);
const supervisorSigPad = ref(null);
const supervisorSigPreview = ref(null);
const localFiles = reactive({});

const formData = reactive({
  siteDetails: {
    siteLocation: '',
    date: new Date().toISOString().slice(0, 10),
    shiftTime: '',
    inspectedBy: ''
  },
  items: Array.from({ length: 5 }, () => ({
    assetId: '',
    serialNo: '',
    category: '',
    visualOk: 'Y',
    guardsOk: 'Y',
    cablesOk: 'Y',
    functionOk: 'Y',
    result: 'PASS'
  })),
  defectsDetails: '',
  signOff: { inspectorName: '', supervisorName: '', inspectorSig: '', supervisorSig: '' }
});

const fetchClientAssets = async () => {
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) return;
    const docRef = doc(db, 'formOptions', 'plantEquipmentItems');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data[cid] && Array.isArray(data[cid])) {
        availableAssets.value = data[cid];
      } else if (data.global && Array.isArray(data.global)) {
        availableAssets.value = data.global;
      }
    }
  } catch (err) {
    console.error("Failed to load client asset options:", err);
  }
};

const onAssetSelected = (idx) => {
  const selectedId = formData.items[idx].assetId;
  const selected = availableAssets.value.find(a => a.value === selectedId);
  if (selected) {
    formData.items[idx].category = selected.category || 'Survey / Plant';
  }
};

const clearItemRow = (idx) => {
  formData.items[idx] = { assetId: '', serialNo: '', category: '', visualOk: 'Y', guardsOk: 'Y', cablesOk: 'Y', functionOk: 'Y', result: 'PASS' };
};

const saveInspectorSig = () => {
  if (inspectorSigPad.value) {
    const data = inspectorSigPad.value.save('image/png');
    if (data) {
      inspectorSigPreview.value = data;
      formData.signOff.inspectorSig = data;
    }
  }
};
const clearInspectorSig = () => {
  if (inspectorSigPad.value) inspectorSigPad.value.clear();
  inspectorSigPreview.value = null;
  formData.signOff.inspectorSig = '';
};

const saveSupervisorSig = () => {
  if (supervisorSigPad.value) {
    const data = supervisorSigPad.value.save('image/png');
    if (data) {
      supervisorSigPreview.value = data;
      formData.signOff.supervisorSig = data;
    }
  }
};
const clearSupervisorSig = () => {
  if (supervisorSigPad.value) supervisorSigPad.value.clear();
  supervisorSigPreview.value = null;
  formData.signOff.supervisorSig = '';
};

const handleFileChange = (key, files) => { localFiles[key] = Array.from(files); };

const submitForm = async () => {
  if (!formData.signOff.inspectorSig) {
    alert("Inspector Digital Signature Seal is required.");
    return;
  }
  isSubmitting.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) throw new Error("Client context missing.");

    let defectUrls = [];
    if (localFiles.defectEvidence) {
      defectUrls = await Promise.all(localFiles.defectEvidence.map(async (file) => {
        const sRef = storageRef(storage, `clients/${cid}/multiPlantInspections/${Date.now()}_${file.name}`);
        await uploadBytes(sRef, file);
        return getDownloadURL(sRef);
      }));
    }

    const result = await httpsCallable(functions, 'submitGenericForm')({
      formIdentifier: 'multiItemInspectionForm',
      targetCollection: 'healthsafetyFormSubmissions',
      submissionData: {
        ...formData,
        defectEvidenceUrls: defectUrls,
        clientId: cid,
        userId: userStore.userProfile?.userId || null,
        userName: formData.signOff.inspectorName || 'Inspector',
        submittedAt: new Date().toISOString(),
        status: 'Pending Review',
        formType: 'multiPlantInspection'
      }
    });

    if (result.data?.success) {
      router.push({ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: result.data.id } });
    } else {
      throw new Error(result.data?.message || "Submission failed.");
    }
  } catch (err) {
    alert("Submission Error: " + err.message);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  if (userStore.isAuthReady) {
    await fetchClientAssets();
  } else {
    const unwatch = userStore.$subscribe(async () => {
      if (userStore.isAuthReady) {
        await fetchClientAssets();
        unwatch();
      }
    });
  }

  if (userStore.userProfile && !formData.siteDetails.inspectedBy) {
    formData.siteDetails.inspectedBy = `${userStore.userProfile.firstName} ${userStore.userProfile.lastName}`;
    formData.signOff.inspectorName = formData.siteDetails.inspectedBy;
  }
  try {
    qrCodeUrl.value = await QRCode.toDataURL(window.location.href, { width: 150, margin: 1 });
    formReference.value = `SS-PEI-2-${Math.floor(1000 + Math.random() * 9000)}`;
  } catch (e) { /* silent */ }
});
</script>

<style scoped>
.form-input { @apply block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-[#0A1630] shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all text-left; }
.form-label { @apply block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2 ml-1 text-left; }
</style>